import React from 'react';
import classNames from 'classnames';
import moment from 'moment';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayPrice,
  isPriceVariationsEnabled,
  requireListingImage,
} from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
} from '../../components';
import { renderPlaceholderPicture } from '../../config/placeholderImages';
import AddToFavoriteButton from '../../extensions/Favorites/components/AddFavoriteButton/AddToFavoriteButton';
import { EXPERIENCES, categoryIcons } from '../../extensions/SearchPage/config/configCategories';
import { getOptionLabels } from '../../extensions/SearchPage/utils';

import { MAPPED_CATEGORY_TO_CARD_STYLE_CONTENT } from '../../extensions/EditListingPage/common/constants';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const EventDateBadge = ({ eventDateTimestamp, intl, categoryLevel1 }) => {
  if (!eventDateTimestamp || (categoryLevel1 !== 'markets_events' && categoryLevel1 !== 'experiences')) return null;

  let dates = [];
  
  // Parse the eventDateTimestamp (could be JSON array or single number for backward compat)
  try {
    if (typeof eventDateTimestamp === 'string') {
      const parsed = JSON.parse(eventDateTimestamp);
      dates = Array.isArray(parsed) ? parsed : [eventDateTimestamp];
    } else if (typeof eventDateTimestamp === 'number') {
      dates = [eventDateTimestamp];
    } else if (Array.isArray(eventDateTimestamp)) {
      dates = eventDateTimestamp;
    }
  } catch (e) {
    // If parsing fails, treat as single number (backward compat)
    if (typeof eventDateTimestamp === 'number') {
      dates = [eventDateTimestamp];
    }
  }

  if (dates.length === 0) return null;

  // Sort dates and get the first one
  const sortedDates = dates.sort((a, b) => a - b);
  const firstEventMoment = moment(sortedDates[0]);
  const formattedDate = firstEventMoment.format('DD MMM YYYY');
  const isUpcoming = firstEventMoment.isAfter(moment());
  const hasMoreDates = sortedDates.length > 1;

  return (
    <div className={css.eventDate}>
      <span className={isUpcoming ? css.upcomingBadge : css.pastBadge}>
        <FormattedMessage
          id={hasMoreDates ? "ListingCard.eventDateMultiple" : "ListingCard.eventDate"}
          values={{ 
            date: formattedDate,
            count: sortedDates.length
          }}
        />
      </span>
    </div>
  );
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, {
  loadAfterInitialRendering: 3000,
});

const PriceMaybe = props => {
  const { price, publicData, config, intl, listingTypeConfig } = props;
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.price} title={priceTitle}>
      {hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : (
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
      )}
    </div>
  );
};

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.currentListing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    currentListing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    showListingImage,
    style,
  } = props;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const { categoryLevel1 } = currentListing.attributes.publicData || {};

  // If listing has an uploaded image, show it
  // Otherwise, always use the B&W category placeholder image
  return (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      {firstImage ? (
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
      ) : (
        renderPlaceholderPicture(categoryLevel1, {
          className: css.rootForImage,
          alt: title,
        })
      )}
    </AspectRatioWrapper>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    showHeartIcon
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);

  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;

  const { listingType, cardStyle, location: { generalAddress = '' } = {}, categoryLevel1 } =
    publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const {
    categoryConfiguration: { categories = [] } = {},
    listing: { listingFields = [] } = {},
  } = config;

  const labels = getOptionLabels(publicData, listingFields);
  const isExperiencesListing = categoryLevel1 === EXPERIENCES;
  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <ListingCardImage
        renderSizes={renderSizes}
        title={title}
        currentListing={currentListing}
        config={config}
        setActivePropsMaybe={setActivePropsMaybe}
        aspectWidth={aspectWidth}
        aspectHeight={aspectHeight}
        variantPrefix={variantPrefix}
        style={cardStyle}
        showListingImage={showListingImage}
      />
      <div className={css.info}>
        <PriceMaybe
          price={price}
          publicData={publicData}
          config={config}
          intl={intl}
          listingTypeConfig={foundListingTypeConfig}
        />
        <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
          {showAuthorInfo ? (
            <div className={css.authorInfo}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
            </div>
          ) : null}
          {!!generalAddress && (
            <div className={css.infoText}>{generalAddress}</div>
          )}
          {categoryLevel1 && (
            <div className={css.category}>
              {categoryIcons[categoryLevel1]}
              <div className={css.infoText}>
                {categories.find(c => c.id === categoryLevel1)?.name}
              </div>
            </div>
          )}
          <EventDateBadge
            eventDateTimestamp={publicData?.eventDatesArray || publicData?.eventDate}
            intl={intl}
            categoryLevel1={categoryLevel1}
          />
          {labels.length > 0 && (
            <div className={css.labels}>
              {labels.map(label => (
                <span key={label} className={css.label}>
                  {label}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      {showHeartIcon && <AddToFavoriteButton listing={listing} />}
    </NamedLink>
  );
};

export default ListingCard;
